<template>
  <div>
    <section class="section">
      <div class="container">
        <h6
          class="title is-6 is-uppercase has-text-left has-text-primary"
          v-t="'Admin.stats_downloaded_capsules_resume_title'"
        ></h6>
        <div class="box">
          <nav class="level">
            <div class="level-left">
              <div class="level-item">
                <div class="field has-addons">
                  <p class="control">
                    <input
                      class="input"
                      type="date"
                      ref="dateInput"
                      data-is-range="true"
                      data-color="primary"
                      :data-today-label="$t('Admin.date_filter_today')"
                      :data-now-label="$t('Admin.date_filter_now')"
                      :data-clear-label="$t('Admin.date_filter_clear')"
                      :data-cancel-label="$t('Admin.date_filter_cancel')"
                      :data-validate-label="$t('Admin.date_filter_validate')"
                      :data-label-from="$t('Admin.date_filter_start_date')"
                      :data-label-to="$t('Admin.date_filter_end_date')"
                    />
                  </p>
                </div>
              </div>
              <div class="level-item" v-if="!loading">
                <div class="control">
                  <a
                    class="button is-dusty-grey has-text-white"
                    @click="exportStatsCSV"
                  >
                    {{ $t("Admin.download_all_stats_csv") }}
                  </a>
                </div>
              </div>
            </div>
            <div class="level-right" v-if="loading">
              <div class="level-item">
                <a class="button is-loading is-inverted"></a>
              </div>
            </div>
          </nav>
          <div class="table-container" v-if="!loading">
            <table class="table is-fullwidth is-bordered">
              <thead>
                <tr>
                  <th class="has-text-centered">
                    {{ $t("Admin.capsule_title") }}
                  </th>
                  <th class="has-text-centered">
                    {{ $t("Admin.students_downloads") }}
                  </th>
                  <th class="has-text-centered">
                    {{ $t("Admin.teacher_downloads") }}
                  </th>
                  <th class="has-text-centered">
                    {{ $t("Admin.total_downloads") }}
                  </th>
                  <th class="has-text-centered">
                    {{ $t("Admin.unique_user_downloads") }}
                  </th>
                </tr>
              </thead>
              <tr v-for="capsule in capsules" :key="capsule._id">
                <td class="subtitle is-6">
                  {{ activeLanguage(capsule.title) }}
                </td>
                <td class="title is-4 has-text-centered">
                  {{ capsule | studentDownloads(statsByDate) }}
                </td>
                <td class="title is-4 has-text-centered">
                  {{ capsule | teacherDownloads(statsByDate) }}
                </td>
                <td class="title is-4 has-text-centered">
                  {{ capsule | allDownloads(statsByDate) }}
                </td>
                <td class="title is-4 has-text-centered">
                  {{ uniqueUsersDownloads(capsule) }}
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <h6
          class="title is-6 is-uppercase has-text-left has-text-primary"
          v-t="'Admin.stats_users_downloads_per_capsule_resume_title'"
        ></h6>
        <div class="box">
          <nav class="level" role="navigation" aria-label="pagination">
            <div class="level-left">
              <div class="level-item">
                <div class="field">
                  <div class="control">
                    <label
                      class="label has-text-left"
                      v-t="'Admin.select_capsule'"
                    ></label>
                    <div class="select">
                      <select @input="changeSelectedCapsule">
                        <option
                          v-for="capsule in capsules"
                          :key="capsule._id"
                          :value="capsule._id"
                        >
                          <p class="subtitle">
                            {{ activeLanguage(capsule.title) }}
                          </p>
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div class="level-item" v-if="selectedCapsule && !loading">
                <div class="control">
                  <label class="label has-text-left">
                    {{ $t("Admin.download_users_list") }}
                  </label>
                  <a
                    class="button is-dusty-grey has-text-white"
                    @click="exportCSV"
                  >
                    CSV
                  </a>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <base-language-selector
                  :activeLanguage="filterActiveLanguage"
                  @changeLanguage="changeFilterLanguage"
                />
              </div>
            </div>
          </nav>
          <div
            class="table-container"
            style="padding-top:1rem;"
            v-if="selectedCapsule && !loading"
          >
            <table class="table is-fullwidth is-bordered">
              <thead>
                <tr>
                  <th>
                    {{ $t("Admin.user_username") }}
                  </th>
                  <th>
                    {{ $t("Admin.user_email") }}
                  </th>
                  <th>
                    {{ $t("Admin.download_date") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="stat in usersByCapsulePerPage(currentPage)"
                  :key="stat.id"
                >
                  <td class="subtitle is-6">
                    {{ stat.user.username }}
                  </td>
                  <td class="subtitle is-6">{{ stat.user.email }}</td>
                  <td class="subtitle is-6">
                    {{ stat.creationDate | moment("DD MMMM YYYY") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <base-pagination
            v-if="totalPages > 1"
            :currentPage="currentPage + 1"
            :pageCount="totalPages"
            @nextPage="currentPage++"
            @previousPage="currentPage--"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { languageManager } from '@/mixins/LanguageManager'
import bulmaCalendar from 'bulma-calendar'
import BasePagination from '@/components/BasePagination'
import BaseLanguageSelector from '@/components/BaseLanguageSelector'
import _ from 'underscore'
export default {
  mixins: [languageManager],
  components: {
    BasePagination,
    BaseLanguageSelector
  },
  props: {
    downloadStats: {
      type: Array,
      default () {
        return []
      }
    }
  },
  data () {
    return {
      selectedCapsule: null,
      currentPage: 0,
      pageLength: 20,
      totalPages: 0,
      endDate: new Date(),
      calendar: null,
      filterRange: {},
      loading: true,
      filterActiveLanguage: this.$store.getters.activeLanguage
    }
  },
  computed: {
    ...mapState({
      capsules () {
        return this.$store.state.capsules.map(capsule => {
          return this.transformObject(capsule)
        })
      },
      rawCapsules () {
        return this.$store.state.capsules
      }
    }),
    usersByCapsule () {
      if (!this.selectedCapsule) {
        return
      }

      const capsuleFiles = this.$options.filters.capsuleFilesIds(
        _.findWhere(this.rawCapsules, { _id: this.selectedCapsule }),
        this.filterActiveLanguage
      )
      let stats = _.filter(this.downloadStats, stat => {
        return _.contains(capsuleFiles, stat.objectId)
      })

      return _.chain(stats).uniq(stat => stat.user.email).sortBy(stat => {
        return -this.$moment(stat.creationDate).format('YYYYMMDD')
      }).value()
    },
    statsByDate () {
      if (!this.filterRange) return this.downloadStats
      else {
        let startDate = this.$moment(this.filterRange.startDate, 'DD/MM/YYYY')
        let endDate = this.$moment(this.filterRange.endDate, 'DD/MM/YYYY')

        return this.downloadStats.filter(stat => {
          let creationDate = this.$moment(stat.creationDate)
          return this.$moment(creationDate, 'DD/MM/YYYY').isBetween(
            startDate,
            endDate,
            null,
            '[]'
          )
        })
      }
    }
  },
  methods: {
    uniqueUsersDownloads (capsule) {
      const capsuleFiles = this.$options.filters.capsuleFilesIds(
        _.findWhere(this.rawCapsules, { _id: capsule._id }),
        this.filterActiveLanguage
      )
      let stats = _.filter(this.statsByDate, stat => {
        return _.contains(capsuleFiles, stat.objectId)
      })
      return _.chain(stats).uniq(stat => stat.user.email).sortBy(stat => {
        return -this.$moment(stat.creationDate).format('YYYYMMDD')
      }).value().length
    },
    changeSelectedCapsule (ev) {
      this.selectedCapsule = ev.target.value
      this.currentPage = 0
    },
    usersByCapsulePerPage () {
      return this.usersByCapsule.slice(
        this.currentPage * this.pageLength,
        (this.currentPage + 1) * this.pageLength
      )
    },
    dateChanged (e) {
      this.filterRange = {
        startDate: this.calendar.startDate,
        endDate: this.calendar.endDate
      }
      // e => (this.date = e.start || null)
    },
    changeFilterLanguage (language) {
      this.filterActiveLanguage = language
    },
    exportCSV () {
      let csvData = this.usersByCapsule.map(stat => {
        return {
          username: stat.user.username,
          email: stat.user.email,
          date: this.$moment(stat.creationDate).format('DD/MM/YYYY'),
          language: this.filterActiveLanguage
        }
      })
      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += [
        Object.keys(csvData[0]).join(';'),
        ...csvData.map(item => Object.values(item).join(';'))
      ]
        .join('\n')
        .replace(/(^\[)|(\]$)/gm, '')

      const data = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', data)
      let selectedCapsule = _.findWhere(this.capsules, {
        _id: this.selectedCapsule
      })
      link.setAttribute(
        'download',
        `mSteam-${this.activeLanguage(selectedCapsule.title)}-${
          this.filterActiveLanguage
        }.csv`
      )
      link.click()
    },
    exportStatsCSV () {
      let dwnStats= this.statsByDate
      let availableLanguages = this.$store.getters.availableLanguages
      let csvData = []
      let moment=this.$moment
      _.each(this.rawCapsules, function(capsule) {
        _.each(availableLanguages, function(language) {
          let teacherCapsuleFiles = [
            ..._.chain(capsule['teacher-downloads'][language])
              .values()
              .flatten()
              .pluck('id')
              .value(),
            
          ]
          
          let studentCapsuleFiles = [
            ..._.chain(capsule['student-downloads'][language])
              .values()
              .flatten()
              .pluck('id')
              .value()
          ]

          let teacherStats = _.filter(dwnStats, stat => {
            return _.contains(teacherCapsuleFiles, stat.objectId)
          })

          _.each(teacherStats, function(stat) {
            csvData.push({
              capsule: capsule.title[language],
              language: language,
              'download type': 'teacher',
              username: stat.user.username,
              email: stat.user.email,
              date: moment(stat.creationDate).format('DD/MM/YYYY')
            })
          })

          let studentStats = _.filter(dwnStats, stat => {
            return _.contains(studentCapsuleFiles, stat.objectId)
          })
          _.each(studentStats, function(stat) {
            csvData.push({
              capsule: capsule.title[language],
              language: language,
              'download type': 'student',
              username: stat.user.username,
              email: stat.user.email,
              date: moment(stat.creationDate).format('DD/MM/YYYY')
            })
          })
        })
        
      })
      if(csvData.length>0){
        let csvContent = 'data:text/csv;charset=utf-8,'
        csvContent += [
          Object.keys(csvData[0]).join(';'),
          ...csvData.map(item => Object.values(item).join(';'))
        ]
          .join('\n')
          .replace(/(^\[)|(\]$)/gm, '')

        const data = encodeURI(csvContent)
        const link = document.createElement('a')
        link.setAttribute('href', data)
        link.setAttribute(
          'download',
          `mSteam-Download-stats.csv`
        )
        link.click()
      }else{
        alert("no data to download")
      }
    }
  },
  mounted () {
    this.calendar = bulmaCalendar.attach(this.$refs.dateInput, {
      endDate: this.endDate,
      lang: this.activeLanguage,
      dateFormat: 'DD/MM/YYYY'
      // maxDate: this.$moment().toISOString()// format('DD/MM/YYYY')
    })[0]
    this.calendar.on('select', this.dateChanged)
  },
  destroyed () {
    // this.calendar.off("select", this.dateChanged);
  },
  watch: {
    capsules (newCapsules) {
      this.selectedCapsule = newCapsules[0]._id
      this.totalPages = this.usersByCapsule
        ? Math.ceil(this.usersByCapsule.length / this.pageLength)
        : 0
    },
    usersByCapsule () {
      this.totalPages = this.usersByCapsule
        ? Math.ceil(this.usersByCapsule.length / this.pageLength)
        : 0
    },
    downloadStats () {
      this.loading = false
    }
  },
  filters: {
    capsuleFilesIds (capsule, filterActiveLanguage) {
      return [
        ..._.chain(capsule['teacher-downloads'][filterActiveLanguage])
          .values()
          .flatten()
          .pluck('id')
          .value(),
        ..._.chain(capsule['student-downloads'][filterActiveLanguage])
          .values()
          .flatten()
          .pluck('id')
          .value()
      ]
    },
    studentDownloads (capsule, stats) {
      if (!capsule['student-downloads']) {
        return 0
      }
      let studentFiles = _.chain(capsule['student-downloads'])
        .values()
        .flatten()
        .pluck('id')
        .value()
      return _.filter(stats, stat => {
        return _.contains(studentFiles, stat.objectId)
      }).length
    },
    teacherDownloads (capsule, stats) {
      if (!capsule['teacher-downloads']) {
        return 0
      }
      let teacherFiles = _.chain(capsule['teacher-downloads'])
        .values()
        .flatten()
        .pluck('id')
        .value()
      return _.filter(stats, stat => {
        return _.contains(teacherFiles, stat.objectId)
      }).length
    },
    allDownloads (capsule, stats) {
      let files = [
        ..._.chain(capsule['teacher-downloads'])
          .values()
          .flatten()
          .pluck('id')
          .value(),
        ..._.chain(capsule['student-downloads'])
          .values()
          .flatten()
          .pluck('id')
          .value()
      ]
      return _.filter(stats, stat => {
        return _.contains(files, stat.objectId)
      }).length
    }
  }
}
</script>

<style  scoped>
</style>
